import React from 'react';
import cn from 'classnames';
import { v4 as uuid } from 'uuid';
import Button, { ButtonVariantType } from '@dealroadshow/uikit/core/components/Button';
import IconAdd from '@dealroadshow/uikit/core/components/Icon/IconAdd';
import IconTrash from '@dealroadshow/uikit/core/components/Icon/IconTrash';
import Tooltip from '@dealroadshow/uikit/core/components/Tooltip';
import { IFinalFormFieldArrayInputComponentProps } from '@/Framework/UI/Organisms/FinalForm/interfaces';
import CusipsListItem from './CusipsListItem';
import { usePortfoliosContext } from '@/finsight/application/PortfolioMonitor/PortfoliosContext';
import { useFormPayloadContext } from '@/finsight/application/PortfolioMonitor/FormPayloadContext';
import { MAX_NUMBER_OF_LISTS_PER_PORTFOLIO } from '@/finsight/ui/components/portfolioMonitor/constants';
import useCancelChangesModal from '@/dataroom/ui/common/Questions/CancelChangesModal/useCancelChangesModal';
import useMySelection from '@/finsight/ui/common/hooks/useMySelection';

import cardStyles from '@dealroadshow/uikit/core/styles/card.scss';
import styles from './createLists.scss';

const CusipsList = ({ fields }: { fields: IFinalFormFieldArrayInputComponentProps['fields'] }) => {
  const { selectedPortfolio } = usePortfoliosContext();
  const { setPayload } = useFormPayloadContext();

  const isMaximumLength = fields.length === MAX_NUMBER_OF_LISTS_PER_PORTFOLIO;

  const addOneList = () => setPayload((prevState) => {
    const item = {
      id: uuid(),
      name: '',
      positions: [],
      portfolioId: selectedPortfolio?.id || null,
      priority: prevState.lists?.length ? prevState.lists?.length + 1 : 1,
      isLocal: true,
    };

    if ('lists' in prevState) {
      return ({
        ...prevState,
        lists: [...prevState.lists, item],
      });
    }
    return ({
      ...prevState,
      lists: [item],
    });
  });

  const removeOneList = (index) => {
    fields.remove(index);
    setPayload((prevState) => ({
      ...prevState,
      lists: prevState.lists.filter((_, i) => i !== index),
    }));
  };

  const {
    isVisible,
    CancelChangesModal,
    hideCanceledModal,
    showCanceledModal,
  } = useCancelChangesModal();

  const selection = useMySelection((item: number) => item);

  const handleDeleteList = (index) => {
    selection.addRows([index]);
    showCanceledModal();
  };

  const confirmDeleteList = () => {
    hideCanceledModal();
    removeOneList(selection.collection[0]);
    selection.reset();
  };

  return (
    <>
      <CancelChangesModal
        isVisible={ isVisible }
        hideCanceledModal={ hideCanceledModal }
        cancelChanges={ confirmDeleteList }
        title="Delete List"
        modalTitle="Delete List"
        modalText="Are you sure you want to delete the selected list from this Portfolio? This action is irreversible."
      />
      { fields.map((row, index) => (
        <div className={ styles.listItem } key={ fields.value[index].id } data-test="cusipListItem">
          <CusipsListItem
            key={ fields.value[index].id }
            row={ row }
            index={ index }
          />
          {
            fields.length > 1 && (
              <Button
                variant={ ButtonVariantType.link }
                dataTest="cusipListRemoveButton"
                onClick={ () => handleDeleteList(index) }
                className={ styles.deleteButton }
              >
                <IconTrash />
              </Button>
            )
          }
        </div>
      )) }
      <div className={ cn(cardStyles.cardInnerVertical, styles.addButtonContainer) }>
        <Tooltip
          disabled={ !isMaximumLength }
          content="You have reached the maximum number &#10; of Lists that can be created in a Portfolio."
        >
          <Button
            variant={ ButtonVariantType.link }
            dataTest="cusipListAddButton"
            onClick={ addOneList }
            disabled={ isMaximumLength }
          >
            <IconAdd />
            Add List
          </Button>
        </Tooltip>
      </div>
    </>
  );
};

export default CusipsList;
